import { memo } from 'react'
import type { ComponentType, UIEvent } from 'react'

import type { InputProps } from 'react-select'
import { components } from 'react-select'
import type { IndicatorProps } from 'react-select/src/components/indicators'
import type { MenuListComponentProps } from 'react-select/src/components/Menu'
import type { OptionProps } from 'react-select/src/components/Option'

import testHandle from '../../utils/testHandle'
import type { DropdownOption } from '../../utils/types'
import Icon from '../Icon/Icon'
import { IconTypes } from '../Icon/Icon.helpers'
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner'

const SCROLL_THRESHOLD_PX = 40

// Sub-components live at module scope (not inside the render) because react-select
// unmounts and remounts the entire dropdown tree whenever a component reference
// changes — redefining them per render breaks typing/scrolling mid-interaction.
// They access the main Select's props via react-select's `selectProps`.

const InputWithAutoComplete = components.Input as ComponentType<InputProps & { autoComplete: string }>

const InputComponent = (props: InputProps) => <InputWithAutoComplete {...props} autoComplete="chrome-off" />

const MenuListComponent = (props: MenuListComponentProps<DropdownOption, false>) => (
  <components.MenuList {...props}>
    <div data-testid={testHandle(`${props.selectProps.id}MenuList`)}>{props.children}</div>
  </components.MenuList>
)

const PaginatedMenuListComponent = (props: MenuListComponentProps<DropdownOption, false>) => {
  const { onMenuScrollToBottom } = props.selectProps

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD_PX) {
      onMenuScrollToBottom?.(e)
    }
  }

  return (
    <components.MenuList {...props}>
      <div data-testid={testHandle(`${props.selectProps.id}MenuList`)} onScroll={handleScroll}>
        {props.children}
      </div>
    </components.MenuList>
  )
}

const DropdownIndicatorComponent = (props: IndicatorProps<DropdownOption, false>) => (
  <components.DropdownIndicator {...props}>
    <Icon name={IconTypes.MagnifyingGlass} />
  </components.DropdownIndicator>
)

const ClearIndicatorComponent = (props: IndicatorProps<DropdownOption, false>) => {
  const {
    children = 'clear',
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles('clearIndicator', props)}
      data-testid={testHandle(`${props.selectProps.id}Clear`)}
    >
      <div>{children}</div>
    </div>
  )
}

const OptionComponent = memo((props: OptionProps<DropdownOption, false>) => (
  <div data-testid={testHandle(`${props.selectProps.id}Option`)} aria-disabled={props.isDisabled}>
    <components.Option {...props}>{props.children}</components.Option>
  </div>
))
OptionComponent.displayName = 'OptionComponent'

const LoadingIndicatorComponent = memo(() => <LoadingSpinner variant="compact" />)
LoadingIndicatorComponent.displayName = 'LoadingIndicatorComponent'

// Stable component maps — all sub-components are module-scoped, so these
// references never change across re-renders.
export const defaultComponents = {
  DropdownIndicator: DropdownIndicatorComponent,
  MenuList: MenuListComponent,
  ClearIndicator: ClearIndicatorComponent,
  Option: OptionComponent,
  Input: InputComponent,
  LoadingIndicator: LoadingIndicatorComponent,
}

export const paginatedComponents = {
  ...defaultComponents,
  MenuList: PaginatedMenuListComponent,
}
